import { LitElement, html, css, nothing } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { PhasrListItem } from './PhasrListItemWebComponent';
import { repeat } from 'lit/directives/repeat.js';
import { LoadMoreOnScrollController } from '../../../ui/reactiveControllers/LoadMoreOnScrollController';
import { isEmpty } from 'lodash';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';

/**
 * @summary This component displays a list of items that can be dynamically loaded on scroll
 * @property {PhasrListItem[]} items - The array of items within this list.
 * @tag gz-phasr-list
 */
@customElement('gz-phasr-list')
export class PhasrListWebComponent extends LitElement {
  static get styles() {
    return [
      styles,
      css`
        :host {
          max-height: 100%;
          overflow: auto;
        }

        gz-progress-spinner {
          padding: calc(2 * var(--spacer)) 0;
          display: flex;
          justify-content: center;
        }
      `,
    ];
  }
  /**
   * The items to display in the list
   */
  @property({ type: Array, attribute: true, reflect: true })
  items: PhasrListItem[] = [];

  /**
   * Whether there are more items to load. This will be set to true/false based on the response from the backend call
   */
  @property({ type: Boolean })
  hasMoreItems = false;

  @property({ type: Boolean })
  isLoading = false;

  @property({ type: String })
  emptyStateMessage = '';

  /**
   * The sentinel element will trigger the load more callback when it's scrolled into view.
   * It should be placed at the end of the list.
   * @private
   */
  @query('#sentinel')
  private sentinel!: HTMLDivElement;

  // Instantiate the controller that will handle the infinite scroll
  private loadMoreController = new LoadMoreOnScrollController(this, {
    onLoadMore: () => this.onLoadMore(),
    // IntersectionObserver options
    observerOptions: {
      root: null, // or this if the component is scrollable
      threshold: 0.1,
    },
    getSentinelElement: () => this.sentinel,
  });

  loadMoreItems?: () => Promise<PhasrListItem[]>;

  private async onLoadMore(): Promise<void> {
    // If we're already loading, skip
    if (this.isLoading) {
      return undefined;
    }

    this.isLoading = true;

    if (this.loadMoreItems) {
      const newItems = await this.loadMoreItems();

      if (newItems.length === 0) {
        // No more items => remove sentinel, which will stop the controller from firing
        this.hasMoreItems = false;
      } else {
        this.items = [...this.items, ...newItems];
      }

      // Done fetching
      this.isLoading = false;
    }
  }
  public cleanup() {
    if (this.loadMoreController) {
      this.loadMoreController.hostDisconnected();
      this.loadMoreController.hostUpdated();
    }
  }

  render() {
    if (isEmpty(this.items) && this.emptyStateMessage !== '' && !this.isLoading) {
      return html`<div class="empty-state">
          <gz-image-repo state="empty">
              <gz-text fontSize="${FontSizes.HEADING_4}" fontWeight="${FontWeights.MEDIUM}">${this.emptyStateMessage}</gz-text>
          </gz-image-repo>
      </div>`;
    }
    return html`
      <div class="container">
        ${repeat(
          this.items,
          (item: PhasrListItem) => item.id,
          item => html`
            <gz-phasr-list-item
              .id=${item.id}
              .name=${item.name}
              .description=${item.description}
              .icon="${item.icon}"
            ></gz-phasr-list-item>
          `
        )}

        <!--
        The sentinel element will trigger the load more callback when it's scrolled into view
        Conditionally render sentinel if we have more items
        -->
        ${this.hasMoreItems
          ? html`
              <div id="sentinel" class="scroll-guard">
                <!-- Show spinner if loading -->
                ${this.isLoading
                  ? html`<gz-progress-spinner indeterminate spinnerWidth="medium"></gz-progress-spinner>`
                  : nothing}
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-phasr-list': PhasrListWebComponent;
  }
}
